import type { ReactNode } from "react";

import type { WikiSummary } from "@/api/db/wiki";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { useWikiList } from "@/hooks/use-wiki";
import { WikiLabels } from "@/lib/labels/wiki";

import { WikiIndex } from "./wiki-index";

export function WikiLayout({
	activeSlug,
	onCreate,
	onCloseCreate,
	children,
}: {
	activeSlug: string | null;
	onCreate: () => void;
	onCloseCreate: () => void;
	children: (state: { pages: WikiSummary[]; isLoading: boolean; isError: boolean }) => ReactNode;
}) {
	const { data, isLoading, isError } = useWikiList();

	const pages = data ?? [];

	return (
		<PageContainer>
			<PageHeader title={WikiLabels.title} description={WikiLabels.subtitle} />

			<div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
				<aside>
					{isError && (
						<div className="surface-panel p-4">
							<p className="text-xs text-destructive">{WikiLabels.indexError}</p>
						</div>
					)}

					{!isError && isLoading && (
						<div className="surface-panel p-3">
							<Skeleton className="h-8 w-full" />
							<div className="mt-3 flex flex-col gap-2">
								<Skeleton className="h-9 w-full" />
								<Skeleton className="h-9 w-full" />
								<Skeleton className="h-9 w-3/4" />
							</div>
						</div>
					)}

					{!isError && !isLoading && (
						<WikiIndex
							pages={pages}
							activeSlug={activeSlug}
							onCreate={onCreate}
							onNavigate={onCloseCreate}
						/>
					)}
				</aside>

				<section className="min-w-0">{children({ pages, isLoading, isError })}</section>
			</div>
		</PageContainer>
	);
}
